import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { AppointmentForm } from "../components/AppointmentForm";
import { ContactForm } from "../components/ContactForm";
import { CLINIC } from "../lib/clinic";

const Contact = () => {
  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact & Appointment"
        title="Get in touch — we usually reply within hours."
        subtitle="Book an appointment, ask a question, or simply say hello. Use the form, WhatsApp or call us directly."
        breadcrumbs={["Contact"]}
      />

      {/* Contact info row */}
      <section className="py-16 bg-paper border-b border-stone-200">
        <div className="container-x">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200">
            {[
              { icon: Phone, title: "Call us", value: CLINIC.phone, href: `tel:${CLINIC.phoneRaw}`, testid: "info-phone" },
              { icon: MessageCircle, title: "WhatsApp", value: "Chat instantly", href: CLINIC.whatsapp, target: "_blank", testid: "info-whatsapp" },
              { icon: Mail, title: "Email", value: CLINIC.email, href: `mailto:${CLINIC.email}`, testid: "info-email" },
              { icon: MapPin, title: "Location", value: CLINIC.location, href: "#map", testid: "info-location" },
            ].map((c, i) => (
              <motion.a
                key={i}
                href={c.href}
                target={c.target}
                rel="noreferrer"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-paper p-8 hover:bg-paper-soft transition-colors group"
                data-testid={c.testid}
              >
                <c.icon className="w-5 h-5 text-brand-tealDark mb-6" strokeWidth={1.3} />
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-goldDark mb-2">{c.title}</div>
                <div className="text-[15px] text-brand-navyDark break-all">{c.value}</div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Booking + contact form */}
      <section id="book" className="section-y bg-paper scroll-mt-24">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7" id="appointment">
            <AppointmentForm />
          </div>
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="bg-brand-navyDark text-white p-9">
              <div className="flex items-center gap-3 mb-7">
                <Clock className="w-5 h-5 text-brand-gold" strokeWidth={1.3} />
                <h3 className="heading-serif text-2xl font-medium text-white">Clinic Hours</h3>
              </div>
              <div className="mb-8">
                {CLINIC.hours.map((h, i) => (
                  <div key={i} className="flex items-center justify-between py-4 border-b border-white/10 last:border-0">
                    <span className="text-stone-300 text-[14px]">{h.day}</span>
                    <span className="text-white text-[14px]">{h.time}</span>
                  </div>
                ))}
              </div>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-5 py-3 hover:bg-white hover:text-brand-navyDark transition-all text-[12px] tracking-[0.06em] uppercase font-medium w-full justify-center"
                data-testid="hours-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Need an off-hours slot? WhatsApp
              </a>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Map */}
      <section id="map" className="pb-28 bg-paper">
        <div className="container-x">
          <div className="mb-10">
            <div className="eyebrow mb-5">Find Us</div>
            <h2 className="heading-serif text-3xl md:text-4xl font-medium text-brand-navyDark">
              Conveniently located in Chandigarh.
            </h2>
          </div>
          <div className="overflow-hidden border border-stone-200">
            <iframe
              title="Clinic location in Chandigarh"
              src="https://www.google.com/maps?q=Chandigarh,India&output=embed"
              width="100%"
              height="480"
              style={{ border: 0, display: "block" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
